<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';

export default {
  components: {
    SettingsFieldSection,
    NextButton,
    NextCheckbox,
    AdaptiveSelect,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isIMAPEnabled: false,
      address: '',
      port: '',
      login: '',
      password: '',
      isSSLEnabled: true,
      authMechanism: 'plain',
      authMechanisms: [
        { key: 1, value: 'plain' },
        { key: 2, value: 'login' },
        { key: 3, value: 'cram-md5' },
      ],
    };
  },
  validations: {
    address: { required },
    port: { required, minLength: minLength(2) },
    login: { required },
    password: { required },
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    authMechanismOptions() {
      return this.authMechanisms.map(option => ({
        label: option.value,
        value: option.value,
      }));
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        imap_enabled,
        imap_address,
        imap_port,
        imap_login,
        imap_password,
        imap_enable_ssl,
        imap_authentication,
      } = this.inbox;
      this.isIMAPEnabled = imap_enabled;
      this.address = imap_address;
      this.port = imap_port;
      this.login = imap_login;
      this.password = imap_password;
      this.isSSLEnabled = imap_enable_ssl;
      this.authMechanism = imap_authentication || 'plain';
    },
    async updateInbox() {
      try {
        this.loading = true;
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            imap_enabled: this.isIMAPEnabled,
            imap_address: this.address,
            imap_port: this.port,
            imap_login: this.login,
            imap_password: this.password,
            imap_enable_ssl: this.isSSLEnabled,
            imap_authentication: this.authMechanism,
          },
        };

        await this.$store.dispatch('inboxes/updateInboxIMAP', payload);
        useAlert(this.$t('INBOX_MGMT.IMAP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message);
      }
    },
  },
};
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.IMAP.TITLE')"
    :help-text="$t('INBOX_MGMT.IMAP.NOTE_TEXT')"
    class="[&>div]:!items-start [&>div>label]:mt-1 mb-4"
  >
    <form @submit.prevent="updateInbox">
      <label class="flex items-center gap-2">
        <NextCheckbox v-model="isIMAPEnabled" />
        {{ $t('INBOX_MGMT.IMAP.TOGGLE_AVAILABILITY') }}
      </label>
      <p>{{ $t('INBOX_MGMT.IMAP.TOGGLE_HELP') }}</p>
      <div v-if="isIMAPEnabled" class="mb-6">
        <woot-input
          v-model="address"
          :class="{ error: v$.address.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.ADDRESS.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.ADDRESS.PLACE_HOLDER')"
          @blur="v$.address.$touch"
        />
        <woot-input
          v-model="port"
          type="number"
          :class="{ error: v$.port.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.PORT.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.PORT.PLACE_HOLDER')"
          @blur="v$.port.$touch"
        />
        <woot-input
          v-model="login"
          :class="{ error: v$.login.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.LOGIN.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.LOGIN.PLACE_HOLDER')"
          @blur="v$.login.$touch"
        />
        <woot-input
          v-model="password"
          :class="{ error: v$.password.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.PASSWORD.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.PASSWORD.PLACE_HOLDER')"
          type="password"
          @blur="v$.password.$touch"
        />
        <label class="flex items-center gap-2">
          <NextCheckbox v-model="isSSLEnabled" />
          {{ $t('INBOX_MGMT.IMAP.ENABLE_SSL') }}
        </label>
        <label class="mb-4 block text-sm font-medium text-n-slate-12">
          <span class="mb-1.5 block">
            {{ $t('INBOX_MGMT.IMAP.AUTH_MECHANISM') }}
          </span>
          <AdaptiveSelect
            v-model="authMechanism"
            :options="authMechanismOptions"
          />
        </label>
      </div>
      <NextButton
        type="submit"
        icon="i-lucide-refresh-cw"
        :label="$t('INBOX_MGMT.IMAP.UPDATE')"
        :is-loading="uiFlags.isUpdatingIMAP"
        :disabled="(v$.$invalid && isIMAPEnabled) || uiFlags.isUpdatingIMAP"
      />
    </form>
  </SettingsFieldSection>
</template>
